import { globalEventBus } from "../events/eventBus";
import { EventEmitter } from "../events/eventEmitter";
import { getWindow } from "../window";
import { TabStateStorage } from "./persistent-storage/tabState";
import { TourStateStorage } from "./persistent-storage/tourState";
import { TabManagerEvents, TabManagerNamespace } from "./tabManager.events";

export interface TabManagerParams {
  tabState: TabStateStorage;
  tourState: TourStateStorage;
}

export class TabManager {
  tabState: TabStateStorage;
  tourState: TourStateStorage;
  tabId: string;
  events = new EventEmitter<typeof TabManagerNamespace, TabManagerEvents>(TabManagerNamespace);

  constructor({ tabState, tourState }: TabManagerParams) {
    this.tabState = tabState;
    this.tourState = tourState;
    this.tabId = this.tabState.getCurrentTabId() ?? Date.now().toString();

    globalEventBus.use("*", (context) => {
      return { ...context, tabId: this.tabId };
    });

    this.init();
  }

  amIActive() {
    return this.tabState.getActiveTabId() === this.tabId;
  }

  private init() {
    this.tabState.setCurrentTabId(this.tabId);
    this.tabState.addTab(this.tabId);
    this.tabState.setActiveTab(this.tabId);

    getWindow().document.addEventListener("visibilitychange", () => this.handleTabVisibilityChange());
    getWindow().addEventListener("unload", () => this.handleTabClose());

    this.listenActiveTabChange();
    this.listenTabsChange();
    this.listenActiveTourTabChange();
  }

  private listenActiveTabChange() {
    this.tabState.onActiveTabChange((tabId?: string) => {
      this.events.emit("activeTabChanged", { tabId }, {}, { skipGlobal: true });

      if (!tabId) {
        this.events.emit("currentTabInactivated", {}, {}, { skipGlobal: true });
        this.events.emit("allTabsInactive", {}, {}, { skipGlobal: true });
        return;
      }

      if (tabId === this.tabId) {
        this.events.emit("currentTabActivated", {}, {}, { skipGlobal: true });
        return;
      }

      this.events.emit("currentTabInactivated", {}, {}, { skipGlobal: true });
    });
  }

  private listenActiveTourTabChange() {
    this.tourState.onTabChange((tabId: string) => {
      if (tabId !== this.tabId) {
        this.events.emit("otherTabTookoverTheTour", {}, {}, { skipGlobal: true });
      }
    });
  }

  private listenTabsChange() {
    this.tabState.onTabsChange((tabs: string[]) => {
      this.events.emit("tabsChanged", { tabs }, {}, { skipGlobal: true });
    });
  }

  private handleTabClose() {
    this.tabState.clearTab(this.tabId);
  }

  private handleTabVisibilityChange() {
    const isTabOnBackground = getWindow().document.visibilityState === "hidden";
    if (isTabOnBackground) {
      this.tabState.clearActiveTab();
      return;
    }

    this.tabState.setActiveTab(this.tabId);
  }
}
